<script lang="ts" setup>
import { VueFinalModal } from "vue-final-modal";

defineEmits(["close"]);

useSubmittedDocumentStore;

const currentDateString = useGenerateFileDateTimestamp();

const fileName = ref(`${currentDateString}_data`);

const documentId = inject("documentId") as any;
const documentStore = useSubmittedDocumentStore(documentId);
const { revision, submittedDocument } = storeToRefs(documentStore);

const checklistStore = useDocumentChecklistStore(documentId);
const {
  useChecklist,
  useTotalEarnedScore,
  useTotalScore,
  useAllChecklistCriteria,
} = storeToRefs(checklistStore);

const paragraphStore = useDocumentRevisionParagraphsStore(documentId);
const {
  paragraphs,

  wordChecklistCells,
} = storeToRefs(paragraphStore);

const annotationStore = useDocumentAnnotationsStore(documentId);
const { allAnnotations: annotations } = storeToRefs(annotationStore);

const appUserData = useAppUserDataStore();
const { commentColor } = storeToRefs(appUserData);

const exportSubmittedDocument = ref(false);
const exportRevision = ref(false);
const exportChecklist = ref(false);
const exportChecklistCriteria = ref(false);
const exportWordChecklistCells = ref(false);
const exportParagraphs = ref(false);
const exportTokens = ref(false);
const exportAnnotations = ref(false);
const exportTotalEarnedScore = ref(false);
const exportTotalScore = ref(false);
const exportCommentColor = ref(false);

const isSaving = ref(false);

const saveData = async () => {
  isSaving.value = true;
  const data = {} as any;

  if (exportSubmittedDocument.value) {
    data["submittedDocument"] = submittedDocument.value;
  }

  if (exportRevision.value) {
    data["revision"] = revision.value;
  }

  if (exportChecklist.value) {
    data["checklist"] = useChecklist.value;
  }

  if (exportChecklistCriteria.value) {
    data["allChecklistCriteria"] = useAllChecklistCriteria.value;
  }

  if (exportWordChecklistCells.value) {
    data["wordChecklistCells"] = wordChecklistCells.value;
  }

  if (exportParagraphs.value) {
    data["paragraphs"] = paragraphs.value;
  }

  if (exportTokens.value) {
    data["tokens"] = tokens.value;
  }

  if (exportAnnotations.value) {
    data["annotations"] = annotations.value;
  }

  if (exportTotalEarnedScore.value) {
    data["totalEarnedScore"] = useTotalEarnedScore.value;
  }

  if (exportTotalScore.value) {
    data["totalScore"] = useTotalScore.value;
  }

  if (exportCommentColor.value) {
    data["commentColor"] = commentColor.value;
  }

  if (Object.keys(data).length === 0) {
    alert("You must choose at least one piece of data to save.");
    isSaving.value = false;
    return;
  }

  await useFetch("/api/sys-admin/log/save-data", {
    method: "POST",
    headers: await useApiHeaders(),

    body: {
      fileName: fileName.value,
      submittedDocument: submittedDocument.value,
      revision: revision.value,
      checklist: useChecklist.value,
      allChecklistCriteria: useAllChecklistCriteria.value,
      wordChecklistCells: wordChecklistCells.value,
      paragraphs: paragraphs.value,
      tokens: tokens.value,
      annotations: annotations.value,
      totalEarnedScore: useTotalEarnedScore.value,
      totalScore: useTotalScore.value,
      commentColor: commentColor.value,
    },
  });

  isSaving.value = false;
};
</script>

<template>
  <VueFinalModal
    class="confirm-modal"
    content-class="confirm-modal-content"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <NuxtLayout name="dialog" @close="$emit('close')">
      <div class="h-full p-4">
        <BaseTextInput
          placeholder="Filename..."
          v-model="fileName"
          class="w-full"
        />
        <div class="flex flex-col">
          <div class="flex flex-row items-center mt-4">
            <BaseToggle
              v-model="exportSubmittedDocument"
              v-if="submittedDocument"
            />
            <span class="ml-2"> Submitted Document? </span>
          </div>
          <div class="flex flex-row items-center mt-4" v-if="revision">
            <BaseToggle v-model="exportRevision" />
            <span class="ml-2"> Revision? </span>
          </div>
          <div class="flex flex-row items-center mt-4" v-if="useChecklist">
            <BaseToggle v-model="exportChecklist" />
            <span class="ml-2"> Checklist? </span>
          </div>

          <div
            class="flex flex-row items-center mt-4"
            v-if="useAllChecklistCriteria"
          >
            <BaseToggle v-model="exportChecklistCriteria" />
            <span class="ml-2"> Checklist Criteria? </span>
          </div>

          <div
            class="flex flex-row items-center mt-4"
            v-if="wordChecklistCells"
          >
            <BaseToggle v-model="exportWordChecklistCells" />
            <span class="ml-2"> Checklist Cells? </span>
          </div>

          <div class="flex flex-row items-center mt-4" v-if="paragraphs">
            <BaseToggle v-model="exportParagraphs" />
            <span class="ml-2"> Paragraphs? </span>
          </div>

          <div class="flex flex-row items-center mt-4" v-if="tokens">
            <BaseToggle v-model="exportTokens" />
            <span class="ml-2"> Tokens? </span>
          </div>

          <div class="flex flex-row items-center mt-4" v-if="annotations">
            <BaseToggle v-model="exportAnnotations" />
            <span class="ml-2"> Annotations? </span>
          </div>

          <div
            class="flex flex-row items-center mt-4"
            v-if="useTotalEarnedScore"
          >
            <BaseToggle v-model="exportTotalEarnedScore" />
            <span class="ml-2"> Total Earned Score? </span>
          </div>

          <div class="flex flex-row items-center mt-4" v-if="useTotalScore">
            <BaseToggle v-model="exportTotalScore" />
            <span class="ml-2"> Total Score? </span>
          </div>

          <div class="flex flex-row items-center mt-4" v-if="commentColor">
            <BaseToggle v-model="exportCommentColor" />
            <span class="ml-2"> Comment Color? </span>
          </div>

          <BaseButton :show-spinner="isSaving" @click="saveData" class="mt-4">
            Save Data
          </BaseButton>
        </div>
      </div>
    </NuxtLayout>
  </VueFinalModal>
</template>

<style>
.confirm-modal {
  z-index: 9999 !important;
}
.confirm-modal-content {
  display: flex;
  flex-direction: column;

  width: 500px;
  height: 80%;
  margin: auto;
  @apply rounded;
  margin-top: 64px;
}
</style>
